import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, CsvImportPanel, DataTable, Pill, type Column , fmtBRL} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import type { PrefeituraFolha, PrefeituraServidor } from "@atlas/sdk";
import { Modal, Field, inp, fmtDataISO, PageHeader } from "./_ui";

/** Calcula corte/repasse a partir do dia de corte do convenio + competencia.
 *  Corte no mes da competencia. Repasse no MESMO DIA do corte, no MES SEGUINTE
 *  (a folha fecha no ultimo dia; o repasse de um mes cai no corte do proximo —
 *  ciclo continuo). Ex.: competencia 202607, corte dia 8 -> corte 2026-07-08,
 *  repasse 2026-08-08. */
function calcularDatas(competencia: string, diaCorte: number): { corte: string; repasse: string } | null {
  const match = /^(\d{4})(\d{2})$/.exec(competencia);
  if (!match) return null;
  const ano = Number(match[1]);
  const mes = Number(match[2]);
  const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const corte = iso(ano, mes, diaCorte);
  const repasseAno = mes === 12 ? ano + 1 : ano;
  const repasseMes = mes === 12 ? 1 : mes + 1;
  return { corte, repasse: iso(repasseAno, repasseMes, diaCorte) };
}

/** Fechamento da folha = ULTIMO DIA do mês da competência (todos os contratos do
 *  mês entram nesta folha; ela fecha no último dia). Ex.: competência 202607 fecha
 *  2026-07-31. */
function previsaoFechamento(competencia: string): string | null {
  const match = /^(\d{4})(\d{2})$/.exec(competencia);
  if (!match) return null;
  const ano = Number(match[1]);
  const mes = Number(match[2]);
  const ultimoDia = new Date(ano, mes, 0).getDate();
  return `${ano}-${String(mes).padStart(2, "0")}-${String(ultimoDia).padStart(2, "0")}`;
}

type FolhaRow = PrefeituraFolha & { movimentacoes: number };



/** Pill de tipo de movimentacao: cores distintas por semantica em vez de tudo
 *  verde "aceita" (bug UX 27/07/2026 — demissao aparecia como "aceita"). */
function movTipoVariant(tipo: string): "aceita" | "pendente" | "rejeitada" | "expirado" {
  const t = tipo.toLowerCase();
  if (t === "admissao" || t === "promocao" || t === "alteracao") return "aceita";
  if (t === "desconto") return "pendente";
  if (t === "aposentadoria") return "expirado";
  if (t === "demissao") return "rejeitada";
  return "pendente";
}
function movTipoLabel(tipo: string): string {
  const map: Record<string, string> = {
    admissao: "Admissão", demissao: "Demissão", aposentadoria: "Aposentadoria",
    promocao: "Promoção", alteracao: "Alteração", desconto: "Desconto",
  };
  return map[tipo.toLowerCase()] ?? tipo;
}

/** Tooltip visual controlado por hover — portalizado pro body (nao e' cortado
 *  pelo overflow do DataTable). Mostra caixinha ACIMA do trigger com setinha
 *  pra baixo. Substitui o title nativo do browser (que nao dispara em varios
 *  contextos). Cliente 28/07/2026. */
function HelpTooltip({ label, texto }: { label: ReactNode; texto: string }) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const abrir = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Posiciona ACIMA do trigger, alinhado a direita — o offset -8 e' pra
    // deixar espaco pra setinha. window.scroll* garante que funcione durante
    // scroll (o rect e' relativo ao viewport).
    setPos({
      top: r.top + window.scrollY - 8,
      left: r.right + window.scrollX,
    });
    setAberto(true);
  };
  const fechar = () => setAberto(false);
  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={abrir}
        onMouseLeave={fechar}
        onFocus={abrir}
        onBlur={fechar}
        tabIndex={0}
        style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4, cursor: "help" }}
      >
        {label}
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 14, height: 14, borderRadius: "50%",
          border: "1px solid var(--text-dim)", color: "var(--text-dim)",
          fontSize: 9, fontWeight: 700, fontFamily: "var(--font-mono)",
        }}>i</span>
      </span>
      {aberto && pos ? createPortal(
        <div style={{
          position: "absolute",
          // Ancora no CANTO INFERIOR DIREITO da caixa (translate -100% em Y e X)
          // pra caixa nascer acima e alinhada a direita do trigger.
          top: pos.top,
          left: pos.left,
          transform: "translate(-100%, -100%)",
          minWidth: 260, maxWidth: 320,
          padding: "10px 12px",
          background: "var(--bg-elev)",
          border: "1px solid var(--border-strong)",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,.35)",
          fontSize: 12, fontWeight: 400, lineHeight: 1.4,
          color: "var(--text)",
          textTransform: "none",
          letterSpacing: 0,
          textAlign: "left",
          zIndex: 9999,
          whiteSpace: "normal",
          pointerEvents: "none",
        }}>
          {texto}
          {/* Setinha apontando pra baixo (borda + fundo em 2 camadas) */}
          <span style={{
            position: "absolute", top: "100%", right: 6,
            width: 0, height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: "6px solid var(--border-strong)",
          }} />
          <span style={{
            position: "absolute", top: "calc(100% - 1px)", right: 7,
            width: 0, height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "5px solid var(--bg-elev)",
          }} />
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export function PrefeituraFolhas() {
  const qc = useQueryClient();
  const [novaOpen, setNovaOpen] = useState(false);
  const [movFolha, setMovFolha] = useState<FolhaRow | null>(null);
  const [descFolha, setDescFolha] = useState<FolhaRow | null>(null);
  // Filtro por servidor — "" = Todos (comportamento default). Quando setado, a
  // tabela mostra apenas as folhas em que o servidor tem ADF/movimentacao, com
  // as colunas Movimentacoes/Descontos ajustadas pra reflect so a fatia dele.
  const [servidorFiltro, setServidorFiltro] = useState<string>("");
  // Poll 5s — quando a averbadora clica Aplicar em folha em outro isolate,
  // a coluna "ADFs aplicadas" aqui atualiza em ate 5s.
  const q = useQuery({
    queryKey: ["prefeitura", "folhas"],
    queryFn: () => atlas.prefeitura.folhas(),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });
  const servidoresQ = useQuery({
    queryKey: ["prefeitura", "servidores-picker"],
    queryFn: () => atlas.prefeitura.servidores(),
    staleTime: 60_000,
  });
  const servidores = servidoresQ.data?.servidores ?? [];
  const folhasBase = q.data?.folhas ?? [];

  // Se ha filtro por servidor, precisa buscar ADFs e movimentacoes de todas as
  // folhas em paralelo pra filtrar por matricula. Cache 30s pra alternar rapido.
  const adfsQueries = useQueries({
    queries: folhasBase.map((f) => ({
      queryKey: ["prefeitura", "adf", f.competencia],
      queryFn: () => atlas.prefeitura.adf(f.competencia),
      staleTime: 30_000,
      enabled: !!servidorFiltro,
    })),
  });
  const movQueries = useQueries({
    queries: folhasBase.map((f) => ({
      queryKey: ["prefeitura", "movimentacoes", f.id],
      queryFn: () => atlas.prefeitura.movimentacoes(f.id),
      staleTime: 30_000,
      enabled: !!servidorFiltro,
    })),
  });

  // Aplica o filtro por servidor — reescreve movimentacoes/valorAplicado por
  // folha pra refletir so o servidor escolhido, e esconde folhas onde ele nao
  // aparece. Quando "Todos" (servidorFiltro === ""), passa as folhas cruas.
  const folhasExibidas: FolhaRow[] = useMemo(() => {
    if (!servidorFiltro) return folhasBase;
    return folhasBase
      .map((folha, idx) => {
        const adfs = (adfsQueries[idx]?.data?.adfs ?? []).filter((a) => a.matricula === servidorFiltro);
        const movs = (movQueries[idx]?.data?.movimentacoes ?? []).filter((m) => m.matricula === servidorFiltro);
        const valorAplicado = adfs.filter((a) => a.status === "aplicada").reduce((s, a) => s + a.valorParcela, 0);
        return { folha, adfsCount: adfs.length, movsCount: movs.length, valorAplicado };
      })
      .filter((r) => r.adfsCount > 0 || r.movsCount > 0)
      .map((r) => ({
        ...r.folha,
        movimentacoes: r.movsCount,
        valorAplicado: r.valorAplicado,
        adfsAplicadas: r.adfsCount,
      }));
  }, [servidorFiltro, folhasBase, adfsQueries, movQueries]);

  const setStatus = useMutation({
    // Consolidar não é ação da prefeitura — só a averbadora consolida (via /averbadora/folhas).
    mutationFn: ({ id, status }: { id: string; status: "aberta" | "fechada" }) => atlas.prefeitura.atualizarFolha(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prefeitura"] }),
  });
  const excluirFolha = useMutation({
    mutationFn: (id: string) => atlas.prefeitura.excluirFolha(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prefeitura"] }),
    onError: (e) => alert((e as Error).message || "Erro ao excluir"),
  });
  // Convenios da prefeitura pra "Realinhar com convenio" — usa o primeiro
  // (todos da prefeitura tem os mesmos dias de corte/repasse geralmente).
  const conveniosQ = useQuery({
    queryKey: ["prefeitura", "convenios"],
    queryFn: () => atlas.prefeitura.convenios(),
    staleTime: 60_000,
  });
  const convenioAtual = conveniosQ.data?.convenios?.[0];
  const realinhar = useMutation({
    mutationFn: ({ id, corte, repasse }: { id: string; corte: string; repasse: string }) =>
      atlas.prefeitura.atualizarFolha(id, { dataCorte: corte, dataRepasse: repasse }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prefeitura"] }),
    onError: (e) => alert((e as Error).message || "Erro ao realinhar"),
  });

  // Compara data da folha com o que o convenio recomenda pra sinalizar
  // divergencia (e habilitar o botao Realinhar).
  const datasSugeridas = (f: FolhaRow) => {
    if (!convenioAtual) return null;
    return calcularDatas(f.competencia, convenioAtual.dataCorte);
  };
  const divergeConvenio = (f: FolhaRow) => {
    const sug = datasSugeridas(f);
    if (!sug) return false;
    return f.dataCorte !== sug.corte || (f.dataRepasse ?? "") !== sug.repasse;
  };

  const columns: Column<FolhaRow>[] = [
    { key: "competencia", header: "Competência", mono: true },
    {
      key: "dataCorte",
      header: "Corte",
      render: (f) => {
        const div = divergeConvenio(f);
        const sug = datasSugeridas(f);
        return (
          <span
            title={div && sug ? `Convênio pede ${fmtDataISO(sug.corte)}` : undefined}
            style={div ? { color: "var(--gold-500)", fontWeight: 600 } : undefined}
          >
            {fmtDataISO(f.dataCorte)}
            {div ? " ⚠" : null}
          </span>
        );
      },
    },
    {
      key: "fechaEm",
      header: "Fecha em",
      render: (f) => {
        const prev = previsaoFechamento(f.competencia);
        return prev
          ? <span style={{ color: "var(--text-muted)" }} title="Fecha no último dia da competência">{fmtDataISO(prev)}</span>
          : <span style={{ color: "var(--text-dim)" }}>—</span>;
      },
    },
    {
      key: "dataRepasse",
      header: "Repasse",
      render: (f) => {
        if (!f.dataRepasse) return "—";
        const div = divergeConvenio(f);
        const sug = datasSugeridas(f);
        return (
          <span
            title={div && sug ? `Convênio pede ${fmtDataISO(sug.repasse)}` : undefined}
            style={div ? { color: "var(--gold-500)", fontWeight: 600 } : undefined}
          >
            {fmtDataISO(f.dataRepasse)}
          </span>
        );
      },
    },
    { key: "movimentacoes", header: "Movimentações", align: "right" },
    {
      key: "valorAplicado",
      header: (
        <HelpTooltip
          label="Descontos aplicados"
          texto="Só ADFs aplicadas via Atlas. Consignações externas do tombamento (BB/Caixa/Bradesco reportadas pela prefeitura) não entram nesta folha — elas aparecem no dashboard como parte de 'Consignações no mês'."
        />
      ),
      align: "right",
      render: (f) => {
        const v = f.valorAplicado ?? 0;
        if (v === 0) return <span style={{ color: "var(--text-dim)" }}>—</span>;
        return <span style={{ color: "var(--emerald-500)", fontWeight: 600 }}>{fmtBRL(v)}</span>;
      },
    },
    { key: "status", header: "Status", render: (f) => <Pill variant={f.status === "aberta" ? "pendente" : "averbado"}>{f.status}</Pill> },
    {
      key: "acoes",
      header: "",
      align: "right",
      render: (f) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button size="sm" variant="ghost" onClick={() => setDescFolha(f)}>Ver descontos</Button>
          {divergeConvenio(f) ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={realinhar.isPending}
              title={`Ajusta as datas para as do convenio (corte dia ${convenioAtual?.dataCorte}, repasse dia ${convenioAtual?.diaRepasse})`}
              onClick={() => {
                const sug = datasSugeridas(f);
                if (!sug) return;
                if (confirm(`Realinhar ${f.competencia}?\n\nCorte: ${fmtDataISO(f.dataCorte)} → ${fmtDataISO(sug.corte)}\nRepasse: ${fmtDataISO(f.dataRepasse)} → ${fmtDataISO(sug.repasse)}`)) {
                  realinhar.mutate({ id: f.id, corte: sug.corte, repasse: sug.repasse });
                }
              }}
            >
              ⚡ Realinhar
            </Button>
          ) : null}
          {f.status === "aberta" ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setMovFolha(f)}>✎ Movimentar</Button>
              {f.movimentacoes === 0 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={excluirFolha.isPending}
                  title="Exclui folha aberta e sem movimentações"
                  onClick={() => {
                    if (confirm(`Excluir a folha ${f.competencia}? Ação irreversível.`)) {
                      excluirFolha.mutate(f.id);
                    }
                  }}
                >
                  🗑 Excluir
                </Button>
              ) : null}
              <Button
                size="sm"
                disabled={f.movimentacoes === 0 || setStatus.isPending}
                title={f.movimentacoes === 0 ? "Importe ao menos 1 movimentação (ou uma linha tipo=desconto) antes de fechar" : undefined}
                onClick={() => setStatus.mutate({ id: f.id, status: "fechada" })}
              >
                Fechar folha
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeader
        title="Folhas de pagamento"
        subtitle="Competência mensal, movimentação de pessoal e datas de corte. Recálculo de margem é automático."
      />

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--text-dim)" }}>
          Servidor:
        </label>
        <select
          value={servidorFiltro}
          onChange={(e) => setServidorFiltro(e.target.value)}
          style={{
            padding: "8px 12px", borderRadius: 8, minWidth: 300,
            border: "1px solid var(--border-strong)",
            background: "var(--bg-elev)", color: "var(--text)", fontSize: 13,
          }}
        >
          <option value="">Todos</option>
          {servidores.map((s: PrefeituraServidor) => (
            <option key={s.matricula} value={s.matricula}>
              {s.nome} — matrícula {s.matricula} · {s.cpfMasked}
            </option>
          ))}
        </select>
        {servidorFiltro ? (
          <Button size="sm" variant="ghost" onClick={() => setServidorFiltro("")}>Limpar filtro</Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        rows={folhasExibidas}
        rowKey={(f) => f.id}
        loading={q.isLoading}
        emptyState={
          servidorFiltro
            ? "Este servidor não aparece em nenhuma folha ainda (sem ADFs e sem movimentações)."
            : "Nenhuma folha. Abra uma competência."
        }
      />

      {novaOpen ? <NovaFolha onClose={() => setNovaOpen(false)} onSaved={() => { setNovaOpen(false); qc.invalidateQueries({ queryKey: ["prefeitura"] }); }} /> : null}
      {movFolha ? <MovModal folha={movFolha} onClose={() => setMovFolha(null)} onDone={() => qc.invalidateQueries({ queryKey: ["prefeitura"] })} /> : null}
      {descFolha ? <DescontosModal folha={descFolha} onClose={() => setDescFolha(null)} /> : null}
    </div>
  );
}

function DescontosModal({ folha, onClose }: { folha: FolhaRow; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["prefeitura", "adf", folha.competencia],
    queryFn: () => atlas.prefeitura.adf(folha.competencia),
    refetchInterval: 5_000,
  });
  const adfs = q.data?.adfs ?? [];
  const aplicadas = adfs.filter((a) => a.status === "aplicada");
  const totalDesconto = aplicadas.reduce((s, a) => s + a.valorParcela, 0);
  const labelProduto = (t?: string) => {
    if (!t) return "Empréstimo";
    const u = t.toUpperCase();
    if (u.includes("BENEF")) return "Cartão Benefício";
    if (u.includes("CARTAO") || u.includes("ECONSIG")) return "Cartão Consignado";
    if (u.includes("REFIN")) return "Portabilidade";
    return "Empréstimo";
  };
  return (
    <Modal title={`Descontos em folha — ${folha.competencia}`} onClose={onClose} maxWidth={880}>
      <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>
        Servidores com desconto averbado nesta competência. A margem já foi confirmada pela averbadora — o valor será debitado no repasse.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, margin: "12px 0" }}>
        <div style={{ padding: 12, background: "var(--bg-elev-2)", borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", fontWeight: 700 }}>Aplicadas</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--emerald-500)" }}>{aplicadas.length}</div>
        </div>
        <div style={{ padding: 12, background: "var(--bg-elev-2)", borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", fontWeight: 700 }}>Total ADFs</div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{adfs.length}</div>
        </div>
        <div style={{ padding: 12, background: "var(--bg-elev-2)", borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", fontWeight: 700 }}>Descontos</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--emerald-500)" }}>{fmtBRL(totalDesconto)}</div>
        </div>
      </div>
      <div style={{ maxHeight: 380, overflow: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
        {q.isLoading ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)" }}>Carregando…</div>
        ) : adfs.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            Nenhuma ADF nesta competência ainda. Quando a averbadora aplicar uma proposta em folha, ela aparece aqui.
          </div>
        ) : (
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg-elev-2)", textTransform: "uppercase", fontSize: 11, letterSpacing: ".06em", color: "var(--text-dim)" }}>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Servidor</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Matrícula</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Produto</th>
                <th style={{ textAlign: "left", padding: "8px 10px" }}>Banco</th>
                <th style={{ textAlign: "right", padding: "8px 10px" }}>Parcela</th>
                <th style={{ textAlign: "center", padding: "8px 10px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {adfs.map((a) => (
                <tr key={a.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "8px 10px" }}>{a.nome}</td>
                  <td style={{ padding: "8px 10px", fontFamily: "var(--font-mono)" }}>{a.matricula}</td>
                  <td style={{ padding: "8px 10px" }}>{labelProduto(a.tipoContrato)}</td>
                  <td style={{ padding: "8px 10px" }}>{a.bancoNome}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtBRL(a.valorParcela)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "center" }}>
                    <Pill variant={a.status === "aplicada" ? "averbado" : a.status === "falha" ? "rejeitada" : "pendente"}>{a.status}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}><Button variant="ghost" onClick={onClose}>Voltar</Button></div>
    </Modal>
  );
}

function NovaFolha({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [competencia, setCompetencia] = useState("");
  const [dataCorte, setDataCorte] = useState("");
  const [dataRepasse, setDataRepasse] = useState("");
  // Pega o convenio pra pre-preencher dataCorte/dataRepasse baseado no
  // diaCorte/diaRepasse configurado. Cliente reportou 24/07/2026: datas na
  // folha nao batiam com o convenio (corte 20/07 quando convenio dizia dia 8).
  const conveniosQ = useQuery({
    queryKey: ["prefeitura", "convenios"],
    queryFn: () => atlas.prefeitura.convenios(),
    staleTime: 60_000,
  });
  const convenio = conveniosQ.data?.convenios?.[0];
  const sugestao = useMemo(() => {
    if (!competencia || !convenio) return null;
    return calcularDatas(competencia, convenio.dataCorte);
  }, [competencia, convenio]);
  // Auto-preenche quando a sugestao muda e os campos estao vazios (nao
  // sobrescreve override manual). Reset ao trocar competencia.
  useEffect(() => {
    if (sugestao) {
      setDataCorte(sugestao.corte);
      setDataRepasse(sugestao.repasse);
    }
  }, [sugestao]);
  const save = useMutation({
    mutationFn: () => atlas.prefeitura.abrirFolha({ competencia, dataCorte, ...(dataRepasse ? { dataRepasse } : {}) }),
    onSuccess: onSaved,
  });
  return (
    <Modal title="Abrir competência de folha" onClose={onClose}>
      <div style={{ display: "grid", gap: 12 }}>
        <Field lbl="Competência (AAAAMM)" hint="ex.: 202608"><input style={inp} value={competencia} onChange={(e) => setCompetencia(e.target.value)} placeholder="202608" /></Field>
        {convenio ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Convênio <b style={{ color: "var(--text)" }}>{convenio.nome}</b> — corte dia <b style={{ color: "var(--text)" }}>{convenio.dataCorte}</b>, repasse dia <b style={{ color: "var(--text)" }}>{convenio.diaRepasse}</b>. As datas abaixo já vêm preenchidas dessa regra — só edite se precisar de exceção.
          </div>
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field lbl="Data de corte"><input type="date" style={inp} value={dataCorte} onChange={(e) => setDataCorte(e.target.value)} /></Field>
          <Field lbl="Data de repasse"><input type="date" style={inp} value={dataRepasse} onChange={(e) => setDataRepasse(e.target.value)} /></Field>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending || !competencia || !dataCorte}>{save.isPending ? "Abrindo…" : "Abrir folha"}</Button>
      </div>
      {save.isError ? <p style={{ color: "var(--danger-500)", marginTop: 12, fontSize: 13 }}>{(save.error as Error).message}</p> : null}
    </Modal>
  );
}

function MovModal({ folha, onClose, onDone }: { folha: FolhaRow; onClose: () => void; onDone: () => void }) {
  const movs = useQuery({ queryKey: ["prefeitura", "movimentacoes", folha.id], queryFn: () => atlas.prefeitura.movimentacoes(folha.id) });
  return (
    <Modal title={`Movimentação — folha ${folha.competencia}`} onClose={onClose} maxWidth={720}>
      <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>Envie admissões, demissões, aposentadorias, promoções e alterações de cargo/salário. As mudanças recalculam a margem automaticamente.</p>
      <CsvImportPanel
        title="Enviar movimentação (CSV)"
        columnsHint="tipo (admissao/demissao/aposentadoria/promocao/alteracao/desconto), matricula, cpf, nome, cargoNovo, salarioNovo, detalhe — use tipo=desconto (demais colunas vazias) pra fechar sem mudança de pessoal"
        templateUrl={atlas.prefeitura.movimentacaoCsvTemplateUrl()}
        onImport={(csv) => atlas.prefeitura.enviarMovimentacao(folha.id, csv)}
        onImported={() => { onDone(); movs.refetch(); }}
      />
      <div style={{ marginTop: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }}>Movimentações registradas ({movs.data?.movimentacoes.length ?? 0})</span>
        <div style={{ marginTop: 8, display: "grid", gap: 6, maxHeight: 220, overflow: "auto" }}>
          {(movs.data?.movimentacoes ?? []).map((m) => (
            <div key={m.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 10px", background: "var(--bg-elev-2)", borderRadius: 8, fontSize: 12 }}>
              <Pill variant={movTipoVariant(m.tipo)}>{movTipoLabel(m.tipo)}</Pill>
              <span style={{ fontFamily: "var(--font-mono)" }}>{m.matricula}</span>
              <span style={{ color: "var(--text-muted)", flex: 1 }}>{m.detalhe}</span>
            </div>
          ))}
          {(movs.data?.movimentacoes.length ?? 0) === 0 ? <span style={{ color: "var(--text-muted)", fontSize: 13 }}>Nenhuma movimentação ainda.</span> : null}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}><Button variant="ghost" onClick={onClose}>Voltar</Button></div>
    </Modal>
  );
}

